import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Download, Loader2, Mail, ArrowLeft, FileText } from "lucide-react";

const labels = {
  fr: { title: "Mes reçus fiscaux", sub: "Retrouvez tous vos reçus fiscaux Larmes en Lumière pour votre déclaration d'impôt.", requestTitle: "Recevoir mes reçus par email", requestSub: "Saisissez l'email utilisé lors de vos dons. Nous vous enverrons un lien sécurisé (valable 24 h) pour consulter et télécharger vos reçus.", emailPlaceholder: "Votre email de donateur", submit: "M'envoyer le lien", sending: "Envoi…", sent: "Si un don confirmé existe pour cet email, un lien vient d'être envoyé.", err: "Erreur. Réessayez plus tard.", loading: "Chargement de vos reçus…", expired: "Lien expiré ou invalide. Merci de refaire une demande.", none: "Aucun reçu confirmé pour cet email.", download: "Télécharger le PDF", amount: "Montant", date: "Date du don", receiptNo: "N° reçu", program: "Programme", back: "Retour à l'accueil" },
  en: { title: "My tax receipts", sub: "Find all your Larmes en Lumière tax receipts for your tax return.", requestTitle: "Get my receipts by email", requestSub: "Enter the email used for your donations. We will send you a secure link (valid 24h) to view and download your receipts.", emailPlaceholder: "Your donor email", submit: "Send me the link", sending: "Sending…", sent: "If a confirmed donation exists for this email, a link was just sent.", err: "Error. Try again later.", loading: "Loading your receipts…", expired: "Link expired or invalid. Please request a new one.", none: "No confirmed receipt for this email.", download: "Download PDF", amount: "Amount", date: "Donation date", receiptNo: "Receipt #", program: "Program", back: "Back to home" },
  de: { title: "Meine Spendenbescheinigungen", sub: "Finden Sie alle Ihre Larmes en Lumière-Spendenbescheinigungen für Ihre Steuererklärung.", requestTitle: "Bescheinigungen per E-Mail erhalten", requestSub: "Geben Sie die für Ihre Spenden verwendete E-Mail ein. Wir senden Ihnen einen sicheren Link (24h gültig).", emailPlaceholder: "Ihre Spender-E-Mail", submit: "Link senden", sending: "Senden…", sent: "Wenn für diese E-Mail eine bestätigte Spende existiert, wurde ein Link gesendet.", err: "Fehler. Bitte später versuchen.", loading: "Ihre Bescheinigungen werden geladen…", expired: "Link abgelaufen oder ungültig.", none: "Keine bestätigte Bescheinigung für diese E-Mail.", download: "PDF herunterladen", amount: "Betrag", date: "Spendendatum", receiptNo: "Beleg-Nr.", program: "Programm", back: "Zur Startseite" },
};

export default function MyReceipts() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { lang } = useI18n();
  const m = labels[lang] || labels.fr;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/donor/history/${token}`).then(r => setData(r.data)).catch(() => setError(labels[lang]?.expired || labels.fr.expired));
  }, [token, lang]);

  const request = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/donor/request-history", { email });
      toast.success(m.sent);
      setEmail("");
    } catch { toast.error(m.err); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="my-receipts-page">
      <section className="hero-gradient py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          <Link to="/" className="text-sm text-deep-blue/60 hover:text-warm-orange inline-flex items-center gap-1 mb-4"><ArrowLeft size={14}/> {m.back}</Link>
          <h1 className="font-display text-deep-blue text-4xl sm:text-5xl leading-tight">{m.title}</h1>
          <p className="text-deep-blue/70 mt-3 max-w-2xl">{m.sub}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          {!token && (
            <form onSubmit={request} className="p-8 rounded-2xl bg-white border border-black/5 max-w-xl">
              <div className="flex items-center gap-2 text-warm-orange">
                <Mail size={18}/>
                <h2 className="font-display text-2xl text-deep-blue">{m.requestTitle}</h2>
              </div>
              <p className="text-sm text-deep-blue/60 mt-2">{m.requestSub}</p>
              <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={m.emailPlaceholder} className="w-full mt-4 p-4 rounded-xl border-2 border-black/5 focus:border-gold outline-none" data-testid="receipt-email"/>
              <button disabled={sending} className="btn-gold mt-4 w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2" data-testid="receipt-submit">
                {sending ? <><Loader2 size={16} className="animate-spin"/> {m.sending}</> : m.submit}
              </button>
            </form>
          )}
          {token && error && <div className="p-6 rounded-2xl bg-warm-orange/10 text-warm-orange border border-warm-orange/30">{error}</div>}
          {token && !error && !data && <div className="p-6 rounded-2xl bg-pearl text-deep-blue/60 italic"><Loader2 className="inline animate-spin mr-2" size={14}/>{m.loading}</div>}
          {data && data.receipts.length === 0 && <div className="p-6 rounded-2xl bg-pearl text-deep-blue/60 italic">{m.none}</div>}
          {data && data.receipts.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-deep-blue/60 mb-4">{data.email} — {data.receipts.length} reçu(s)</p>
              {data.receipts.map(r => (
                <div key={r.id} className="p-5 rounded-2xl bg-white border border-black/5 flex flex-wrap items-center gap-4" data-testid={`receipt-item-${r.id}`}>
                  <FileText className="text-gold flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-deep-blue">{r.amount?.toFixed(2)} € · {r.receipt_number}</div>
                    <div className="text-xs text-deep-blue/60 mt-1">{m.date}: {(r.confirmed_at || r.created_at || '').slice(0, 10)} · {r.program}</div>
                  </div>
                  {r.receipt_token && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${r.receipt_token}`} target="_blank" rel="noopener noreferrer" className="btn-gold px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid={`receipt-dl-${r.id}`}>
                      <Download size={14}/> {m.download}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
